import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { COMPANY, HERO_IMAGE, SERVICES, GALLERY, OWNERS, SERVICE_FEES, formatGHS } from "@/data/content";
import { Plane, ShieldCheck, MessagesSquare, FileCheck2, ArrowRight, Phone, CheckCircle2 } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

const FEATURES = [
  { icon: FileCheck2, title: "Track Applications", desc: "Follow every step of your visa or tour application in real time." },
  { icon: ShieldCheck, title: "Secure Uploads", desc: "Upload passports and documents safely to your private portal." },
  { icon: MessagesSquare, title: "Direct Messaging", desc: "Chat with our team and get answers whenever you need them." },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-brand-navy text-brand-text">
      <Navbar variant="landing" />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Luxury travel" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-navy/55" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-transparent to-brand-navy/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full pt-24">
          <motion.div initial="hidden" animate="show" variants={fade} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-brand-gold text-xs tracking-[0.25em] uppercase mb-6">
              <span className="h-px w-8 bg-brand-gold" /> {COMPANY.tagline}
            </span>
            <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] tracking-tight mb-6">
              Travel the world with <span className="italic"><span className="text-yellow-300">Fragua</span> <span className="text-brand-gold">Global</span></span>
            </h1>
            <p className="text-lg text-white/75 leading-relaxed mb-9 max-w-xl">
              Visa processing, flight & hotel bookings and unforgettable tour packages — managed end-to-end by Kingsley Antwi & Aaron Appiagyei.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button onClick={() => navigate("/register")} data-testid="hero-start-btn" className="rounded-full h-12 px-8 bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold text-base">
                Start Your Journey <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a href={`https://wa.me/${COMPANY.whatsappIntl}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn">
                <Button variant="outline" className="rounded-full h-12 px-8 border-white/25 text-white hover:bg-white/10 text-base">
                  <Phone className="w-4 h-4 mr-2" /> WhatsApp Us
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
              className="p-8 rounded-2xl bg-brand-surface/50 border border-white/5 hover:border-brand-gold/30 transition-colors">
              <f.icon className="w-8 h-8 text-brand-gold mb-5" />
              <h3 className="font-display text-xl mb-2">{f.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-16 max-w-2xl">
            <span className="text-brand-gold text-xs tracking-[0.25em] uppercase">What We Do</span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-4">Our Services</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {SERVICES.map((s, i) => (
              <motion.div key={s.key} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                data-testid={`service-card-${s.key}`}
                className="group relative rounded-3xl overflow-hidden border border-white/10 min-h-[440px] flex items-end">
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-transparent" />
                <div className="relative p-9">
                  <h3 className="font-display text-2xl mb-3">{s.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed mb-5 max-w-md">{s.desc}</p>
                  <p className="text-sm text-white/60 mb-5">From <span className="font-display text-brand-gold text-base">{formatGHS(SERVICE_FEES[s.key])}</span></p>
                  <Button onClick={() => navigate("/register")} className="rounded-full bg-brand-gold/90 text-brand-navy hover:bg-brand-gold font-semibold">
                    Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="py-24 bg-brand-surface/30 grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-14 max-w-2xl">
            <span className="text-brand-gold text-xs tracking-[0.25em] uppercase">Portfolio</span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-4">Visas & Destinations</h2>
            <p className="text-white/60 mt-4">A glimpse of successful visas processed and destinations our clients now call their happy place.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 auto-rows-[220px]">
            {GALLERY.map((g, i) => (
              <motion.div key={i} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                className={`group relative rounded-2xl overflow-hidden border border-white/10 ${g.tall ? "row-span-2" : ""}`}>
                <img src={g.url} alt={g.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-5">
                  <span className="text-sm font-medium text-brand-gold">{g.label}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / OWNERS */}
      <section id="about" className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-brand-gold text-xs tracking-[0.25em] uppercase">Who We Are</span>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-4 mb-6">Founded on trust & wanderlust</h2>
              <p className="text-white/70 leading-relaxed mb-4">
                Fragua Global Limited was built by two travel enthusiasts, Kingsley Antwi and Aaron Appiagyei, with a shared mission: to make world travel accessible, transparent and stress-free for every client.
              </p>
              <ul className="space-y-3 mt-6">
                {["Personalised, end-to-end service", "Transparent status tracking", "Trusted, hands-on visa expertise"].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-white/80">
                    <CheckCircle2 className="w-5 h-5 text-brand-gold" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {OWNERS.map((o, i) => (
                <motion.div key={o.name} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
                  className="rounded-2xl overflow-hidden border border-white/10 bg-brand-surface/50">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={o.image} alt={o.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg">{o.name}</h3>
                    <p className="text-brand-gold text-xs">{o.role}</p>
                    {o.bio && <p className="text-white/55 text-sm leading-relaxed mt-3">{o.bio}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-3xl overflow-hidden border border-brand-gold/20 bg-brand-surface p-12 md:p-16 text-center grain">
            <Plane className="w-10 h-10 text-brand-gold mx-auto mb-6" />
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-4">Ready to travel with confidence?</h2>
            <p className="text-white/65 max-w-xl mx-auto mb-8">Create your account to submit an application, upload documents, make payments and track your progress.</p>
            <Button onClick={() => navigate("/register")} data-testid="cta-register-btn" className="rounded-full h-12 px-10 bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold text-base">
              Create Free Account
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
